import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import {
  buildCoParentInviteReturnUrl,
  persistPostAuthReturnUrl,
} from '../../../auth/helpers/post-auth-return.helper';
import { AuthSessionService } from '../../../auth/services/auth-session.service';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { FamilyHubService } from '../../services/family-hub.service';

@Component({
  selector: 'app-co-parent-invite-accept',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ParentButtonComponent, RouterLink],
  templateUrl: './co-parent-invite-accept.component.html',
  styleUrl: './co-parent-invite-accept.component.scss',
})
export class CoParentInviteAcceptComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #session = inject(AuthSessionService);
  #familyHub = inject(FamilyHubService);
  #identityNav = inject(IdentityNavigationService);

  status = signal<
    'loading' | 'ready' | 'success' | 'rejected' | 'error' | 'need-auth'
  >('loading');
  invite = signal<AuthGen.CoParentInvite | null>(null);
  messageKey = signal('app.familyHub.acceptPage.loading');
  needsKyc = signal(false);
  token = '';
  inviteId = '';

  readonly titleKey = computed(() =>
    (this.invite()?.targetRole ?? '').trim() === 'EXTENDED'
      ? 'app.familyHub.acceptPage.titleCaregiver'
      : 'app.familyHub.acceptPage.title',
  );
  ngOnInit(): void {
    void this.bootstrap();
  }

  private inviteReturnUrl(): string {
    if (this.token) {
      return buildCoParentInviteReturnUrl(this.token);
    }
    if (this.inviteId) {
      return `/co-parent-invite?inviteId=${encodeURIComponent(this.inviteId)}`;
    }
    return '/co-parent-invite';
  }

  private async bootstrap(): Promise<void> {
    this.token =
      this.#route.snapshot.paramMap.get('token') ??
      this.#route.snapshot.queryParamMap.get('token') ??
      '';
    this.inviteId =
      this.#route.snapshot.queryParamMap.get('inviteId') ?? '';

    if (!this.token && !this.inviteId) {
      this.status.set('error');
      this.messageKey.set('app.familyHub.acceptPage.missingToken');
      return;
    }

    if (!this.#session.isAuthenticated()) {
      // inviteId-only recovery requires an existing session (claim already happened).
      if (!this.token) {
        this.status.set('need-auth');
        this.messageKey.set('app.familyHub.acceptPage.needAuth');
        void this.#router.navigate(['/auth'], { replaceUrl: true });
        return;
      }
      const returnUrl = this.inviteReturnUrl();
      persistPostAuthReturnUrl(returnUrl);
      this.status.set('need-auth');
      this.messageKey.set('app.familyHub.acceptPage.needAuth');
      void this.#router.navigate(['/auth'], {
        queryParams: { returnUrl },
        replaceUrl: true,
      });
      return;
    }

    try {
      if (this.token) {
        const preview = await this.#familyHub.getInvitePreview(this.token);
        this.invite.set(preview);
        this.inviteId = preview.id || this.inviteId;
      } else {
        const list = await this.#familyHub.listMyInvites();
        const match =
          list.incoming?.find((inv) => inv.id === this.inviteId) ?? null;
        if (!match) {
          this.status.set('error');
          this.messageKey.set('app.familyHub.acceptPage.invalid');
          return;
        }
        this.invite.set(match);
      }

      const preview = this.invite();
      if (!preview) {
        this.status.set('error');
        this.messageKey.set('app.familyHub.acceptPage.invalid');
        return;
      }
      if (preview.alreadyMember) {
        this.status.set('success');
        this.messageKey.set('app.familyHub.acceptPage.alreadyMember');
        await this.continueAfterJoin();
        return;
      }
      if (preview.status !== 'pending') {
        this.status.set('error');
        this.messageKey.set('app.familyHub.acceptPage.notPending');
        return;
      }
      this.status.set('ready');
      this.messageKey.set(
        (preview.targetRole ?? '').trim() === 'EXTENDED'
          ? 'app.familyHub.acceptPage.readyCaregiver'
          : 'app.familyHub.acceptPage.ready',
      );
    } catch {
      this.status.set('error');
      this.messageKey.set('app.familyHub.acceptPage.invalid');
    }
  }

  goAuth(): void {
    if (!this.token) {
      void this.#router.navigate(['/auth']);
      return;
    }
    const returnUrl = this.inviteReturnUrl();
    persistPostAuthReturnUrl(returnUrl);
    void this.#router.navigate(['/auth'], {
      queryParams: { returnUrl },
    });
  }

  async accept(): Promise<void> {
    try {
      const dto: AuthGen.CoParentInviteTokenDto = this.token
        ? { token: this.token }
        : { inviteId: this.inviteId };
      const targetRole = (this.invite()?.targetRole ?? 'CO_PARENT').trim();
      if (targetRole === 'EXTENDED') {
        await this.#familyHub.acceptCaregiverInvite.mutateAsync(dto);
      } else {
        await this.#familyHub.acceptInvite.mutateAsync(dto);
      }
      this.status.set('success');
      this.messageKey.set(
        targetRole === 'EXTENDED'
          ? 'app.familyHub.acceptPage.successCaregiver'
          : 'app.familyHub.acceptPage.success',
      );
      await this.continueAfterJoin();
    } catch {
      this.status.set('error');
      this.messageKey.set('app.familyHub.acceptPage.acceptFailed');
    }
  }

  async reject(): Promise<void> {
    try {
      const dto: AuthGen.CoParentInviteTokenDto = this.token
        ? { token: this.token }
        : { inviteId: this.inviteId };
      await this.#familyHub.rejectInvite.mutateAsync(dto);
      this.status.set('rejected');
      this.messageKey.set('app.familyHub.acceptPage.rejected');
    } catch {
      this.status.set('error');
      this.messageKey.set('app.familyHub.acceptPage.rejectFailed');
    }
  }

  goVerify(): void {
    void this.#identityNav.navigateToVerification();
  }

  /** Same path as primary after auth: identity verify (bounces home if already verified / layer off). */
  private async continueAfterJoin(): Promise<void> {
    this.needsKyc.set(true);
    this.messageKey.set('app.familyHub.acceptPage.successNeedKyc');
    await this.#identityNav.navigateToVerification();
  }
}
